/* eslint-disable custom/no-hardcoded-colors */
import { withOnClickTracking } from 'auto-tracking';
import { Icon, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

type PeerPilotDescriptionProps = {
  price?: string;
};

export const PeerPilotDescription = ({ price }: PeerPilotDescriptionProps) => {
  const { t } = useTranslation('homepage');

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <NewLaunchBadge>{t('peerpilot.section.newLaunchBadge')}</NewLaunchBadge>
        <MainHeading>{t('peerpilot.section.mainHeading')}</MainHeading>
      </div>

      <Subtitle>{t('peerpilot.section.subtitle')}</Subtitle>

      <FeaturesGrid>
        <FeatureCard>
          <Image src="/peerpilot-search.svg" height={60} width={60} alt={''} />
          <FeatureTitle>
            {t('peerpilot.features.criticalOverview.title')}
          </FeatureTitle>
          <FeatureDescription>
            {t('peerpilot.features.criticalOverview.description')}
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard>
          <Image src="/pie-chart.svg" height={60} width={60} alt={''} />
          <FeatureTitle>
            {t('peerpilot.features.methodologyReview.title')}
          </FeatureTitle>
          <FeatureDescription>
            {t('peerpilot.features.methodologyReview.description')}
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard>
          <Image src="/peerpilot-file.svg" height={60} width={60} alt={''} />
          <FeatureTitle>
            {t('peerpilot.features.literatureReview.title')}
          </FeatureTitle>
          <FeatureDescription>
            {t('peerpilot.features.literatureReview.description')}
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard>
          <Image src="/peerpilot-secure.svg" height={60} width={60} alt={''} />
          <FeatureTitle>
            {t('peerpilot.features.guidelineCompliance.title')}
          </FeatureTitle>
          <FeatureDescription>
            {t('peerpilot.features.guidelineCompliance.description')}
          </FeatureDescription>
        </FeatureCard>
      </FeaturesGrid>

      <ImportanceSection>
        <ImportanceHeading>
          {t('peerpilot.importance.heading')}
        </ImportanceHeading>

        <BenefitsGrid>
          <BenefitCard>
            <BenefitTitle>
              {t('peerpilot.importance.reviewerFeedback.title')}
            </BenefitTitle>
            <BenefitDescription>
              {t('peerpilot.importance.reviewerFeedback.description')}
            </BenefitDescription>
          </BenefitCard>

          <DividerLine>
            <Image src="/gradient-line.svg" height={1} width={1} alt={''} />
          </DividerLine>

          <BenefitCard>
            <BenefitTitle>
              {t('peerpilot.importance.technicalScores.title')}
            </BenefitTitle>
            <BenefitDescription>
              {t('peerpilot.importance.technicalScores.description')}
            </BenefitDescription>
          </BenefitCard>

          <DividerLine>
            <Image src="/gradient-line.svg" height={1} width={1} alt={''} />
          </DividerLine>

          <BenefitCard>
            <BenefitTitle>
              {t('peerpilot.importance.actionableSummary.title')}
            </BenefitTitle>
            <BenefitDescription>
              {t('peerpilot.importance.actionableSummary.description', {
                priceText: price,
              })}
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>
      </ImportanceSection>

      <ActionsContainer>
        <TrackedDownloadButton
          as="a"
          href="/PeerPilot-Sample-File.pdf"
          download="PeerPilot-Sample-File.pdf"
          trackingName="download-sample-report"
          trackingProperties={{
            sectionName: 'PeerPilot Section',
            elementName: 'download_sample_button',
          }}
        >
          {t('peerpilot.section.downloadSampleButton')}{' '}
          <Icon name="ArrowDownToLine" variant={'regular'} />
        </TrackedDownloadButton>
      </ActionsContainer>
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  margin: '0 auto',
});

const NewLaunchBadge = styled('div', {
  background: 'linear-gradient(90deg, #1E5431 -63.06%, #0A8734 100%)',
  color: theme.colors.white,
  padding: '6px 12px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const MainHeading = styled('div', {
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: '32px',
  color: '#051E57',
  margin: 0,
  marginBottom: '16px',
  marginTop: '12px',
  fontFamily: 'IBM Plex Serif',
});

const Subtitle = styled('div', {
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '28px',
  color: '#051E57',
  maxWidth: '800px',
  marginBottom: '40px',
  fontFamily: 'IBM Plex Sans',
});

const FeaturesGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '30px',
  width: '100%',
  marginBottom: '50px',
});

const FeatureCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  padding: '24px 26px',
  backgroundColor: '#F6FAFF',
  borderRadius: '12px',
  border: `1px solid #CEEAF7`,
  textAlign: 'center',
  minHeight: '230px',
});

const FeatureTitle = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#051E57',
});

const FeatureDescription = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontSize: '14px',
  lineHeight: '20px',
  color: '#051E57',
  margin: 0,
});

const ImportanceSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  width: '100%',
});

const ImportanceHeading = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '40px',
  color: '#051E57',
  margin: 0,
  textAlign: 'center',
});

const BenefitsGrid = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  width: '100%',
  maxWidth: '1170px',
});

const DividerLine = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  margin: '12px 0',
});

const BenefitCard = styled('div', {
  padding: '24px',
  textAlign: 'center',
  minWidth: '330px',
  minHeight: '70px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const BenefitTitle = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#051E57',
  margin: '0 0 8px 0',
});

const BenefitDescription = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#051E57',
  margin: 0,
});

const ActionsContainer = styled('div', {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '16px',
  justifyContent: 'center',
  marginTop: '10px',
});

// const ErrorStateContainer = styled('div', {
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
//   gap: '16px',
//   width: '100%',
// });

// const ErrorContainer = styled('div', {
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
//   width: '100%',
//   gap: '16px',
// });

const DownloadButton = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 24px',
  paddingTop: '0px',
  color: theme.colors.blue9,
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.colors.blue10,
    transform: 'translateY(-1px)',
  },
});

const TrackedDownloadButton = withOnClickTracking(DownloadButton);
